import { useEffect, useMemo, useState } from 'react'
import { Conversation, Participant } from '@twilio/conversations'

import { t } from 'translations'
import { getUserName, getUserNames } from '../../../services/utilFunctions/getUserName'

const useParticipantsTyping = (conversation: Conversation): { participantsTyping: string[]; displayParticipantsTyping: string } => {
  const [participantsTyping, setParticipantsTyping] = useState<string[]>([])

  const displayParticipantsTyping = useMemo(() => {
    if (participantsTyping.length === 0) {
      return ''
    }

    const participantsUserNames = participantsTyping
      .map(userId => {
        return getUserName(userId)
      })
      .filter(Boolean) as string[]

    if (participantsUserNames.length === 1) {
      return `${participantsUserNames[0]} ${t('chat.CONVO.IS_TYPING')}`
    }

    const userNamesText = getUserNames(participantsTyping, { delimiter: ',' })

    return `${userNamesText} ${t('chat.CONVO.ARE_TYPING')}`
  }, [participantsTyping])

  useEffect(() => {
    const onTypingChanged = (participant: Participant, shouldAdd: boolean) => {
      if (shouldAdd) {
        setParticipantsTyping(state => {
          if (!participant.identity) return state

          if (state.includes(participant.identity)) return state

          return [...state, participant.identity]
        })
      } else {
        setParticipantsTyping(state => state.filter(p => p !== participant.identity))
      }
    }
    const onTypingStarted = (participant: Participant) => onTypingChanged(participant, true)
    const onTypingEnded = (participant: Participant) => onTypingChanged(participant, false)

    conversation.on('typingStarted', onTypingStarted)
    conversation.on('typingEnded', onTypingEnded)

    return () => {
      conversation.off('typingStarted', onTypingStarted)
      conversation.off('typingEnded', onTypingEnded)
    }
  }, [conversation])

  return { participantsTyping: participantsTyping, displayParticipantsTyping }
}

export { useParticipantsTyping }
